import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updatePatient, patientProfileUpdate, removePatientProfileImage } from '../../redux/patientSlice';
import { useTranslation } from 'react-i18next';
import { restrictnavigate, tryingnavigate, clearPendingNavigation } from '../../redux/blockingSlice';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import Cropper from 'react-easy-crop';
import { Edit, X, Check, Loader2, Send, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import { checkEmailExists, checkMobileExists, sendOtp, verifyOtp } from '../../redux/authSlice';
import useAxiosAuth from '../../hooks/useAxiosAuth';
import { docPatCreationCountryList } from '../../redux/masterSlice';
import CountryTimeZoneSelect from '../common/CountryTimeZoneSelect';

import { getFileURL } from '../../utils/fileURL';
import CountrySelect from '../common/CountrySelect';
import CountryFlag from '../common/CountryFlag';
import MasterLabel from '../common/MasterLabel';
import GenderSelect from '../common/GenderSelect';
import PhoneCountryDropdown from '../common/PhoneCountryDropdown';
import { getGenderOptions, formatFirstName, formatSurname, sanitizePostalCode, postalCodePattern, POSTAL_CODE_MAX_LENGTH } from '../../utils/personFields';

const formatDobDisplay = (dob) => {
    if (!dob) return '-';
    const str = String(dob).trim();
    // Parse the date and extract UTC components to avoid timezone shift (±1 day)
    let date;
    if (str.includes('T') || /^\d{4}-\d{2}-\d{2}$/.test(str)) date = new Date(str);
    else if (/^\d{2}[/-]\d{2}[/-]\d{4}$/.test(str)) { const p = str.split(/[/-]/); date = new Date(`${p[2]}-${p[1]}-${p[0]}`); }
    else date = new Date(str);
    if (!date || isNaN(date.getTime())) return '-';
    // Use UTC getters to avoid local timezone shifting the date
    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const year = date.getUTCFullYear();
    return `${day}/${month}/${year}`;
};

// Convert a UTC date to YYYY-MM-DD string using UTC getters (avoids ±1 day timezone shift)
const toUTCDateInputValue = (dob) => {
    if (!dob) return '';
    const d = new Date(dob);
    if (isNaN(d.getTime())) return '';
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
};

// PORTED FROM THE TENANT APP, with a `readOnly` mode it does not have.
//
// WHAT THIS FORM SHOWS SUPER ADMIN: THE PERSON, NOT A HOSPITAL'S COPY.
// Since one-patient-row (owner, 22 Sep 2026) there is ONE `patients` row per
// human, holding the whole profile, and a hospital only ALLOCATES its own
// patient number against it (doc/one-patient-row.md §2). So the id this tab is
// given is the person's Mitra One Care id and the fields below are the same at
// every hospital — there is no "their record at Chitra" to choose between.
//
// WHY READ-ONLY IS NOT OPTIONAL HERE. The row belongs to the PATIENT: they
// maintain it in their own app, and `PUT /patients/:id` answers 409
// MOC_OWNED_FIELD on any CHANGE or CLEAR of a claimed person's identity,
// contact or profile field — for a hospital desk and for the platform alike.
// An Edit button would therefore offer a save the API refuses. The second
// reason is older and still true: this form saves every field at once, so if a
// response ever comes back thinned (projectPatient's identity-only shape) a
// save would write the withheld values back as blanks.
//
// So `readOnly` hides Edit, hides the avatar's change control, and blocks the
// auto-open-edit-on-incomplete-profile path (a profile can legitimately read as
// incomplete here). The caller renders the banner that says whose profile this
// is and where a hospital's own details are edited instead.
const PatientProfileTab = ({ patient, readOnly = false }) => {
    const { t } = useTranslation(['patientprofiletab', 'common']);
    const dispatch = useDispatch();
    const [isEditMode, setIsEditMode] = useState(false);
    const [isIncompleteMode, setIsIncompleteMode] = useState(false);
    const [incompleteFields, setIncompleteFields] = useState([]);
    const { trynavigate } = useSelector((state) => state.blocking);
    const { isLoading, removeLoading, selectedPatient } = useSelector((state) => state.patient);
    const { countries, countryTimezoneList } = useSelector((state) => state.master);
    const fileInputRef = useRef();
    const [profileImage, setProfileImage] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        surname: '',
        gender: '',
        dob: '',
        age: 0,
        occupation: '',
        email: '',
        phone: '',
        altPhone: '',
        address: '',
        additional: '',
        country: '',
        countryId: '',
        city: '',
        zipcode: '',
        timeZone: '',
    });
    const selectedCountry = countryTimezoneList?.find(c => c.countryId === formData.countryId) || null;
    const [errors, setErrors] = useState({});

    useEffect(() => {
        dispatch(docPatCreationCountryList());
    }, [dispatch]);

    const axiosInstance = useAxiosAuth();
    const { emailCheckLoading, mobileCheckLoading, sendOtpLoading, verifyOtpLoading } = useSelector((state) => state.auth || {});

    // Phone logic states
    const [phoneStatus, setPhoneStatus] = useState(null); // null: not checked, true: available, false: exists
    const phoneCheckTimeoutRef = useRef(null);
    const lastCheckedPhoneRef = useRef(''); // Track last checked phone number
    const isPhoneCheckInProgressRef = useRef(false); // Track if phone check is in progress

    // Phone OTP states
    const [phoneOtpSent, setPhoneOtpSent] = useState(false);
    const [phoneOtpCode, setPhoneOtpCode] = useState('');
    const [phoneOtpVerified, setPhoneOtpVerified] = useState(false);
    const [phoneOtpError, setPhoneOtpError] = useState('');
    const [phoneOtpTimer, setPhoneOtpTimer] = useState(0);
    const phoneOtpTimerRef = useRef(null);
    const phoneOtpVerifyingRef = useRef(false);

    // Email logic states
    const [emailStatus, setEmailStatus] = useState(null);
    const emailCheckTimeoutRef = useRef(null);
    const lastCheckedEmailRef = useRef('');
    const isEmailCheckInProgressRef = useRef(false);

    // Email OTP states
    const [emailOtpSent, setEmailOtpSent] = useState(false);
    const [emailOtpCode, setEmailOtpCode] = useState('');
    const [emailOtpVerified, setEmailOtpVerified] = useState(false);
    const [emailOtpError, setEmailOtpError] = useState('');
    const [emailOtpTimer, setEmailOtpTimer] = useState(0);
    const emailOtpTimerRef = useRef(null);
    const emailOtpVerifyingRef = useRef(false);

    const dispatchRef = useRef(dispatch);
    const axiosInstanceRef = useRef(axiosInstance);

    useEffect(() => {
        dispatchRef.current = dispatch;
        axiosInstanceRef.current = axiosInstance;
    }, [dispatch, axiosInstance]);

    // OTP related functions
    const resetOtpEmailState = () => {
        setEmailOtpSent(false);
        setEmailOtpCode('');
        setEmailOtpVerified(false);
        setEmailOtpError('');
        setEmailOtpTimer(0);
        if (emailOtpTimerRef.current) clearInterval(emailOtpTimerRef.current);
        emailOtpVerifyingRef.current = false;
    };

    const resetOtpPhoneState = () => {
        setPhoneOtpSent(false);
        setPhoneOtpCode('');
        setPhoneOtpVerified(false);
        setPhoneOtpError('');
        setPhoneOtpTimer(0);
        if (phoneOtpTimerRef.current) clearInterval(phoneOtpTimerRef.current);
        phoneOtpVerifyingRef.current = false;
    };

    const startOtpTimer = (setTimer, timerRef) => {
        if (timerRef.current) clearInterval(timerRef.current);
        setTimer(60);
        timerRef.current = setInterval(() => {
            setTimer(prev => {
                if (prev <= 1) {
                    clearInterval(timerRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const handleSendPhoneOtp = async () => {
        setPhoneOtpError('');
        try {
            const result = await dispatch(sendOtp({ phone: formData.phone.trim(), axiosInstance })).unwrap();
            if (result?.success) {
                setPhoneOtpSent(true);
                setPhoneOtpCode('');
                setPhoneOtpVerified(false);
                startOtpTimer(setPhoneOtpTimer, phoneOtpTimerRef);
            }
        } catch (err) {
            setPhoneOtpError(typeof err === 'string' ? err : t('patientProfile.failed_send_otp'));
        }
    };

    const handleVerifyPhoneOtp = useCallback(async (code) => {
        const otpVal = code ?? phoneOtpCode;
        if (phoneOtpVerifyingRef.current || otpVal.length !== 6) return;
        phoneOtpVerifyingRef.current = true;
        setPhoneOtpError('');
        try {
            const result = await dispatch(verifyOtp({ phone: formData.phone.trim(), otpCode: otpVal, axiosInstance })).unwrap();
            if (result?.success) {
                setPhoneOtpVerified(true);
                if (phoneOtpTimerRef.current) clearInterval(phoneOtpTimerRef.current);
                setPhoneOtpTimer(0);
            } else {
                setPhoneOtpError(t('patientProfile.invalid_otp'));
            }
        } catch (err) {
            setPhoneOtpError(typeof err === 'string' ? err : t('patientProfile.invalid_otp'));
        } finally {
            phoneOtpVerifyingRef.current = false;
        }
    }, [phoneOtpCode, dispatch, axiosInstance, formData.phone]);

    const handleSendEmailOtp = async () => {
        setEmailOtpError('');
        try {
            const result = await dispatch(sendOtp({ email: formData.email.trim(), axiosInstance })).unwrap();
            if (result?.success) {
                setEmailOtpSent(true);
                setEmailOtpCode('');
                setEmailOtpVerified(false);
                startOtpTimer(setEmailOtpTimer, emailOtpTimerRef);
            }
        } catch (err) {
            setEmailOtpError(typeof err === 'string' ? err : t('patientProfile.failed_send_otp'));
        }
    };

    const handleVerifyEmailOtp = useCallback(async (code) => {
        const otpVal = code ?? emailOtpCode;
        if (emailOtpVerifyingRef.current || otpVal.length !== 6) return;
        emailOtpVerifyingRef.current = true;
        setEmailOtpError('');
        try {
            const result = await dispatch(verifyOtp({ email: formData.email.trim(), otpCode: otpVal, axiosInstance })).unwrap();
            if (result?.success) {
                setEmailOtpVerified(true);
                if (emailOtpTimerRef.current) clearInterval(emailOtpTimerRef.current);
                setEmailOtpTimer(0);
            } else {
                setEmailOtpError(t('patientProfile.invalid_otp'));
            }
        } catch (err) {
            setEmailOtpError(typeof err === 'string' ? err : t('patientProfile.invalid_otp'));
        } finally {
            emailOtpVerifyingRef.current = false;
        }
    }, [emailOtpCode, dispatch, axiosInstance, formData.email]);

    // Cleanup OTP timers on unmount
    useEffect(() => {
        return () => {
            if (phoneOtpTimerRef.current) clearInterval(phoneOtpTimerRef.current);
            if (phoneCheckTimeoutRef.current) clearTimeout(phoneCheckTimeoutRef.current);
            if (emailOtpTimerRef.current) clearInterval(emailOtpTimerRef.current);
            if (emailCheckTimeoutRef.current) clearTimeout(emailCheckTimeoutRef.current);
        };
    }, []);

    const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    // Debounced email check
    useEffect(() => {
        if (!isEditMode) return;

        if (emailCheckTimeoutRef.current) {
            clearTimeout(emailCheckTimeoutRef.current);
        }

        const email = formData.email?.trim() || '';
        const originalEmail = selectedPatient?.email || patient?.email || '';

        if (originalEmail && email === originalEmail) {
            setEmailStatus(true);
            setEmailOtpVerified(true);
            lastCheckedEmailRef.current = email;
            return;
        }

        if (!email || !emailRegex.test(email)) {
            setEmailStatus(null);
            setEmailOtpVerified(false);
            lastCheckedEmailRef.current = '';
            return;
        }

        if (lastCheckedEmailRef.current === email || isEmailCheckInProgressRef.current) {
            return;
        }

        emailCheckTimeoutRef.current = setTimeout(async () => {
            const trimmedEmail = formData.email.trim();
            if (!trimmedEmail || !emailRegex.test(trimmedEmail) || lastCheckedEmailRef.current === trimmedEmail || isEmailCheckInProgressRef.current) {
                return;
            }

            isEmailCheckInProgressRef.current = true;
            lastCheckedEmailRef.current = trimmedEmail;

            try {
                const resultAction = await dispatchRef.current(checkEmailExists({
                    email: trimmedEmail,
                    axiosInstance: axiosInstanceRef.current
                }));

                if (formData.email.trim() === trimmedEmail) {
                    if (checkEmailExists.fulfilled.match(resultAction)) {
                        setEmailStatus(!resultAction.payload.exists);
                        if (resultAction.payload.exists) {
                            setEmailOtpVerified(false);
                        }
                    } else {
                        setEmailStatus(null);
                        lastCheckedEmailRef.current = '';
                    }
                }
            } catch (error) {
                console.error('Error checking email:', error);
                if (formData.email.trim() === trimmedEmail) {
                    setEmailStatus(null);
                    lastCheckedEmailRef.current = '';
                }
            } finally {
                isEmailCheckInProgressRef.current = false;
            }
        }, 500);
    }, [formData.email, isEditMode, selectedPatient?.email, patient?.email]);

    // Debounced phone check
    useEffect(() => {
        if (!isEditMode) return;

        if (phoneCheckTimeoutRef.current) {
            clearTimeout(phoneCheckTimeoutRef.current);
        }

        const phone = formData.phone?.trim() || '';
        const originalPhone = selectedPatient?.phone || patient?.phone || '';

        // Treat original phone as valid
        if (originalPhone && phone === originalPhone) {
            setPhoneStatus(true);
            setPhoneOtpVerified(true);
            lastCheckedPhoneRef.current = phone;
            return;
        }

        const phoneMin = selectedCountry?.mobileMinLength || 10;
        const phoneMax = selectedCountry?.mobileMaxLength || 10;
        const isValidPhone = selectedCountry && new RegExp(`^\\d{${phoneMin},${phoneMax}}$`).test(phone);

        if (!phone || !isValidPhone) {
            setPhoneStatus(null);
            setPhoneOtpVerified(false);
            lastCheckedPhoneRef.current = '';
            return;
        }

        if (lastCheckedPhoneRef.current === phone || isPhoneCheckInProgressRef.current) {
            return;
        }

        phoneCheckTimeoutRef.current = setTimeout(async () => {
            const trimmedPhone = formData.phone.trim();
            if (!trimmedPhone || lastCheckedPhoneRef.current === trimmedPhone || isPhoneCheckInProgressRef.current) {
                return;
            }

            isPhoneCheckInProgressRef.current = true;
            lastCheckedPhoneRef.current = trimmedPhone;

            try {
                const resultAction = await dispatchRef.current(checkMobileExists({
                    phone: trimmedPhone,
                    axiosInstance: axiosInstanceRef.current
                }));

                if (formData.phone.trim() === trimmedPhone) {
                    if (checkMobileExists.fulfilled.match(resultAction)) {
                        setPhoneStatus(!resultAction.payload.exists);
                        if (resultAction.payload.exists) {
                            setPhoneOtpVerified(false);
                        }
                    } else {
                        setPhoneStatus(null);
                        lastCheckedPhoneRef.current = '';
                    }
                }
            } catch (error) {
                console.error('Error checking phone:', error);
                if (formData.phone.trim() === trimmedPhone) {
                    setPhoneStatus(null);
                    lastCheckedPhoneRef.current = '';
                }
            } finally {
                isPhoneCheckInProgressRef.current = false;
            }
        }, 500);
    }, [formData.phone, isEditMode, formData.countryId, selectedCountry, selectedPatient?.phone, patient?.phone]);

    // === Profile Image Modal States ===
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [imageSrc, setImageSrc] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [isCropping, setIsCropping] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const userRole = localStorage.getItem('Role');
    // PORTED FROM THE TENANT APP. There the contact fields are gated on the
    // literal role 'admin'; super_admin never reaches that screen, so nobody
    // noticed. On the platform side the same person IS the administrator —
    // and the API already treats super_admin as a superset of admin — so a
    // straight copy would have rendered email / phone / country permanently
    // read-only with no explanation. One named flag instead of four copies of
    // the same expression, so the next role added is a one-line change.
    const isAdminLevel = userRole === 'admin' || userRole === 'Admin' || userRole === 'super_admin';

    const profileOwner = userRole === 'patient' ? t('patientprofiletab.your') : t('patientprofiletab.patient');

    useEffect(() => {
        if (trynavigate && isEditMode) {
            Swal.fire({
                title: t('patientprofiletab.editing_in_progress'),
                text: t('patientprofiletab.editing_in_progress_text', { profileOwner }),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: t('patientprofiletab.abort'),
                confirmButtonColor: '#4ABDBD',
                cancelButtonText: t('patientprofiletab.cancel'),
                cancelButtonColor: '#4ABDBD',
                allowOutsideClick: false,
                allowEscapeKey: false,
            }).then((result) => {
                if (result.isConfirmed) {
                    // Discard — preserve pendingNavigation for the parent consumer.
                    dispatch(tryingnavigate(false));
                    handleEditToggle();
                } else if (result.dismiss === Swal.DismissReason.cancel) {
                    // Stay — drop the captured destination.
                    dispatch(tryingnavigate(false));
                    dispatch(clearPendingNavigation());
                }
            });
        }
    }, [trynavigate, dispatch, t, isEditMode]);

    // Check profile status and identify incomplete fields
    useEffect(() => {
        const currentPatient = selectedPatient || patient;
        if (currentPatient && currentPatient.patientId === patient.patientId) {
            // Check if profile is incomplete
            const isIncomplete = currentPatient.profileStatus === 'incomplete';
            setIsIncompleteMode(isIncomplete);

            if (isIncomplete) {
                // Identify which fields are missing (empty/null) - excluding email, country, occupation, and additional
                const missing = [];
                if (!currentPatient.surname || currentPatient.surname.trim() === '') missing.push('surname');
                if (!currentPatient.gender || currentPatient.gender.trim() === '') missing.push('gender');
                if (!currentPatient.dob) missing.push('dob');
                // Occupation is optional, so not included in incomplete fields
                if (!currentPatient.phone || currentPatient.phone.trim() === '') missing.push('phone');
                if (!currentPatient.address || currentPatient.address.trim() === '') missing.push('address');
                if (!currentPatient.city || currentPatient.city.trim() === '') missing.push('city');
                if (!currentPatient.zipCode || currentPatient.zipCode.trim() === '') missing.push('zipcode');

                setIncompleteFields(missing);

                // Automatically enable edit mode if there are incomplete fields.
                // Never in readOnly: the "missing" fields there are the ones the
                // API withheld, so this would open an edit form primed to blank
                // the real values.
                if (missing.length > 0 && !readOnly) {
                    setIsEditMode(true);
                }
            } else {
                setIncompleteFields([]);
            }
        }
    }, [selectedPatient, patient, readOnly]);

    useEffect(() => {
        const currentPatient = selectedPatient || patient;
        if (currentPatient) {
            // Normalize gender: convert "others" to "other" to match select dropdown values
            const normalizedGender = currentPatient.gender === 'others' ? 'other' : (currentPatient.gender || '');

            const matchedCountry = countryTimezoneList?.find(c =>
                c.countryName === currentPatient.country ||
                c.countryId === currentPatient.country?.countryId ||
                c.countryId === currentPatient.country ||
                c.countryId === currentPatient.countryId
            );

            setFormData({
                name: currentPatient.name || '',
                surname: currentPatient.surname || '',
                gender: normalizedGender,
                dob: toUTCDateInputValue(currentPatient.dob),
                age: currentPatient.dob ? calculateAge(currentPatient.dob) : 0,
                occupation: currentPatient.occupation || '',
                email: currentPatient.email || '',
                phone: currentPatient.phone || '',
                altPhone: currentPatient.altPhone || '',
                address: currentPatient.address || '',
                additional: currentPatient.additional || '',
                country: currentPatient.country?.countryName || currentPatient.country || '',
                countryId: matchedCountry?.countryId || currentPatient.country?.countryId || currentPatient.countryId || formData?.countryId || '',
                city: currentPatient.city || '',
                zipcode: currentPatient.zipCode || '',
                timeZone: currentPatient.timeZone || formData?.timeZone || '',
            });
            setProfileImage(currentPatient.image || null);
        }
    }, [patient, selectedPatient, countryTimezoneList]);

    // Check if all incomplete fields are filled
    const areAllIncompleteFieldsFilled = () => {
        if (!isIncompleteMode || incompleteFields.length === 0) return true;

        // Check if ALL incomplete fields now have values
        for (const field of incompleteFields) {
            if (field === 'zipcode') {
                if (!formData.zipcode || formData.zipcode.trim() === '') return false;
            } else {
                if (!formData[field] || formData[field].trim() === '') return false;
            }
        }

        return true; // All incomplete fields are filled
    };

    const validateForm = (data) => {
        const newErrors = {};
        const nameRegex = /^[a-zA-ZÀ-ÿ\s\-']+$/; // Letters (incl. accented), spaces, hyphens, and apostrophes
        const alphaSpaceRegex = /^[A-Za-zÀ-ÿ\s]+$/;
        const addressRegex = /^[A-Za-zÀ-ÿ0-9,\.\-\s]+$/;
        const safeCharsRegex = /^[A-Za-zÀ-ÿ0-9,\.\-\s]*$/;

        // Dynamic country validation rules
        const phoneMin = selectedCountry?.mobileMinLength || 10;
        const phoneMax = selectedCountry?.mobileMaxLength || 10;
        const altMin = selectedCountry?.altMinLength || phoneMin;
        const altMax = selectedCountry?.altMaxLength || phoneMax;
        const zipMin = selectedCountry?.zipMinLength || 5;
        const zipMax = selectedCountry?.zipMaxLength || 6;

        // In incomplete mode, only validate fields that have values (not forcing empty fields to be required)
        if (isIncompleteMode) {
            // Name validation (if provided)
            if (data.name && data.name.trim()) {
                if (!nameRegex.test(data.name.trim())) {
                    newErrors.name = t('patientProfile.name_invalid_chars');
                } else if (data.name.trim().length < 2 || data.name.trim().length > 20) {
                    newErrors.name = t('patientProfile.name_length_2_20');
                }
            }

            // Surname validation (if provided)
            if (data.surname && data.surname.trim()) {
                if (!nameRegex.test(data.surname.trim())) {
                    newErrors.surname = t('patientProfile.surname_invalid_chars');
                } else if (data.surname.trim().length < 2 || data.surname.trim().length > 20) {
                    newErrors.surname = t('patientProfile.surname_length_2_20');
                }
            }

            // Gender validation (if provided)
            if (data.gender && data.gender.trim()) {
                // Gender is valid if it's one of the canonical options
                const validGenders = ['male', 'female', 'not_communicated', 'other'];
                if (!validGenders.includes(data.gender.toLowerCase())) {
                    newErrors.gender = t('patientprofiletab.error_gender_invalid') || 'Invalid gender';
                }
            }

            // DOB validation (if provided)
            if (data.dob && data.dob.trim()) {
                const dobDate = new Date(data.dob);
                const today = new Date();
                if (dobDate > today) {
                    newErrors.dob = t('patientprofiletab.error_dob_future') || t('patientProfile.dob_not_future');
                } else if (dobDate.getFullYear() < 1900) {
                    newErrors.dob = t('patientProfile.dob_after_1900');
                }
            }

            // Occupation validation (if provided) - OPTIONAL FIELD
            if (data.occupation && data.occupation.trim()) {
                if (!alphaSpaceRegex.test(data.occupation)) newErrors.occupation = t('patientprofiletab.error_occupation_invalid');
                else if (data.occupation.length < 5 || data.occupation.length > 25) newErrors.occupation = t('patientprofiletab.error_occupation_length');
            }

            // Address validation (if provided)
            if (data.address && data.address.trim()) {
                if (!addressRegex.test(data.address)) newErrors.address = t('patientprofiletab.error_address_invalid');
                else if (data.address.length < 5 || data.address.length > 50) newErrors.address = t('patientprofiletab.error_address_length');
            }

            // Email validation (when changed)
            if (data.email && data.email.trim() && (selectedPatient?.email || patient?.email) !== data.email.trim()) {
                const emailVal = data.email.trim();
                const emailRegexLocal = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                if (!emailRegexLocal.test(emailVal)) {
                    newErrors.email = t('patientProfile.invalid_email_format');
                } else if (emailStatus === false) {
                    newErrors.email = t('patientProfile.email_already_exists');
                } else if (emailStatus === null && !emailCheckLoading) {
                    newErrors.email = t('patientProfile.please_verify_email');
                } else if (emailStatus === true && !emailOtpVerified) {
                    newErrors.email = t('patientProfile.verify_email_otp');
                }
            }

            // Phone validation (if provided) - Dynamic country-specific
            if (data.phone && data.phone.trim()) {
                const phoneRegex = new RegExp(`^\\d{${phoneMin},${phoneMax}}$`);
                if (!phoneRegex.test(data.phone)) {
                    newErrors.phone = t('patientprofiletab.error_phone_invalid');
                }

                if (!newErrors.phone) {
                    if (phoneStatus === false) {
                        newErrors.phone = t('patientProfile.phone_already_exists');
                    } else if (phoneStatus === null && !mobileCheckLoading) {
                        newErrors.phone = t('patientProfile.please_verify_phone');
                    } else if (phoneStatus === true && !phoneOtpVerified) {
                        newErrors.phone = t('patientProfile.verify_phone_otp');
                    }
                }
            }

            // City validation (if provided in incomplete mode)
            if (data.city && data.city.trim()) {
                if (!alphaSpaceRegex.test(data.city)) {
                    newErrors.city = t('patientProfile.city_invalid_chars');
                } else if (data.city.length < 5 || data.city.length > 25) {
                    newErrors.city = t('patientProfile.city_length_5_25');
                }
            }

            // Zipcode validation (if provided) - Dynamic country-specific
            if (data.zipcode && data.zipcode.trim()) {
                const zipRegex = postalCodePattern(zipMin, zipMax);
                if (!zipRegex.test(data.zipcode)) {
                    newErrors.zipcode = t('patientprofiletab.error_zipcode_invalid');
                }
            }
        } else {
            // Normal mode - strict validation
            // Name - MANDATORY
            if (!data.name.trim()) {
                newErrors.name = t('patientprofiletab.error_name_required');
            } else if (!nameRegex.test(data.name.trim())) {
                newErrors.name = t('patientProfile.name_invalid_chars');
            } else if (data.name.trim().length < 2 || data.name.trim().length > 20) {
                newErrors.name = t('patientProfile.name_length_2_20');
            }

            // Surname - MANDATORY
            if (!data.surname.trim()) {
                newErrors.surname = t('patientprofiletab.error_surname_required');
            } else if (!nameRegex.test(data.surname.trim())) {
                newErrors.surname = t('patientProfile.surname_invalid_chars');
            } else if (data.surname.trim().length < 2 || data.surname.trim().length > 20) {
                newErrors.surname = t('patientProfile.surname_length_2_20');
            }

            // Gender - MANDATORY
            if (!data.gender || !data.gender.trim()) {
                newErrors.gender = t('patientprofiletab.error_gender_required') || 'Gender is required';
            }

            // DOB - MANDATORY
            if (!data.dob || !data.dob.trim()) {
                newErrors.dob = t('patientprofiletab.error_dob_required') || 'Date of birth is required';
            } else {
                const dobDate = new Date(data.dob);
                const today = new Date();
                if (dobDate > today) {
                    newErrors.dob = t('patientProfile.dob_not_future');
                } else if (dobDate.getFullYear() < 1900) {
                    newErrors.dob = t('patientProfile.dob_after_1900');
                }
            }

            // Occupation - OPTIONAL (only validate if provided)
            if (data.occupation && data.occupation.trim()) {
                if (!alphaSpaceRegex.test(data.occupation)) newErrors.occupation = t('patientprofiletab.error_occupation_invalid');
                else if (data.occupation.length < 5 || data.occupation.length > 25) newErrors.occupation = t('patientprofiletab.error_occupation_length');
            }

            // Email validation (when changed)
            if (data.email && data.email.trim() && (selectedPatient?.email || patient?.email) !== data.email.trim()) {
                const emailVal = data.email.trim();
                const emailRegexLocal = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                if (!emailRegexLocal.test(emailVal)) {
                    newErrors.email = t('patientProfile.invalid_email_format');
                } else if (emailStatus === false) {
                    newErrors.email = t('patientProfile.email_already_exists');
                } else if (emailStatus === null && !emailCheckLoading) {
                    newErrors.email = t('patientProfile.please_verify_email');
                } else if (emailStatus === true && !emailOtpVerified) {
                    newErrors.email = t('patientProfile.verify_email_otp');
                }
            }

            // Phone - MANDATORY
            if (!data.phone || !data.phone.trim()) {
                newErrors.phone = t('patientprofiletab.error_phone_required') || 'Phone is required';
            } else {
                const phoneRegex = new RegExp(`^\\d{${phoneMin},${phoneMax}}$`);
                if (!phoneRegex.test(data.phone)) {
                    newErrors.phone = t('patientprofiletab.error_phone_invalid');
                }

                if (!newErrors.phone) {
                    if (phoneStatus === false) {
                        newErrors.phone = t('patientProfile.phone_already_exists');
                    } else if (phoneStatus === null && !mobileCheckLoading) {
                        newErrors.phone = t('patientProfile.please_verify_phone');
                    } else if (phoneStatus === true && !phoneOtpVerified) {
                        newErrors.phone = t('patientProfile.verify_phone_otp');
                    }
                }
            }

            // Alt Phone validation (optional)
            if (data.altPhone && data.altPhone.trim()) {
                const altRegex = new RegExp(`^\\d{${altMin},${altMax}}$`);
                if (!altRegex.test(data.altPhone)) {
                    newErrors.altPhone = t('patientprofiletab.error_phone_invalid');
                }
            }

            // Address - MANDATORY
            if (!data.address.trim()) newErrors.address = t('patientprofiletab.error_address_required');
            else if (!addressRegex.test(data.address)) newErrors.address = t('patientprofiletab.error_address_invalid');
            else if (data.address.length < 5 || data.address.length > 50) newErrors.address = t('patientprofiletab.error_address_length');

            // City - MANDATORY
            if (!data.city || !data.city.trim()) {
                newErrors.city = t('patientprofiletab.error_city_required') || 'City is required';
            } else {
                if (!alphaSpaceRegex.test(data.city)) {
                    newErrors.city = t('patientProfile.city_invalid_chars');
                } else if (data.city.length < 5 || data.city.length > 25) {
                    newErrors.city = t('patientProfile.city_length_5_25');
                }
            }

            // Zipcode - MANDATORY
            if (!data.zipcode || !data.zipcode.trim()) {
                newErrors.zipcode = t('patientprofiletab.error_zipcode_required') || 'Zipcode is required';
            } else {
                const zipRegex = postalCodePattern(zipMin, zipMax);
                if (!zipRegex.test(data.zipcode)) {
                    newErrors.zipcode = t('patientprofiletab.error_zipcode_invalid');
                }
            }
        }

        // Additional info validation (optional field)
        if (data.additional && data.additional.length > 80) newErrors.additional = t('patientprofiletab.error_additional_length');
        else if (data.additional && !safeCharsRegex.test(data.additional)) newErrors.additional = t('patientprofiletab.error_additional_invalid');

        return newErrors;
    };

    const calculateAge = (dob) => {
        const birthDate = new Date(dob);
        const today = new Date();
        // Use UTC getters for birthDate to avoid timezone shift on DOB
        let age = today.getFullYear() - birthDate.getUTCFullYear();
        const monthDiff = today.getMonth() - birthDate.getUTCMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getUTCDate())) {
            age--;
        }
        return age;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let processedValue = value;

        // First name: only the first character uppercased.
        if (name === 'name' && processedValue.length > 0) {
            processedValue = formatFirstName(processedValue);
        }
        // Surname (family name): first character uppercased, rest as typed.
        if (name === 'surname' && processedValue.length > 0) {
            processedValue = formatSurname(processedValue);
        }

        // Phone fields: digits only. Postal code: letters and digits, capped at 10.
        if (name === 'phone' || name === 'altPhone') {
            processedValue = value.replace(/[^0-9]/g, '');
        }
        if (name === 'zipcode') {
            processedValue = sanitizePostalCode(value);
        }

        // Emails are stored and compared lowercase.
        if (name === 'email') {
            processedValue = processedValue.toLowerCase();
        }

        // Restrict name and surname to letters, spaces, hyphens, and apostrophes only
        if ((name === 'name' || name === 'surname') && processedValue && !/^[a-zA-ZÀ-ÿ\s\-']*$/.test(processedValue)) {
            return;
        }

        // Validate city to only accept letters and spaces
        if (name === 'city') {
            if (value && !/^[A-Za-zÀ-ÿ\s]*$/.test(value)) {
                return; // Don't update if contains numbers or special characters
            }
        }

        const updatedFormData = { ...formData, [name]: processedValue };
        if (name === 'dob') {
            updatedFormData.age = calculateAge(processedValue);
        }
        setFormData(updatedFormData);
        setErrors((prev) => ({ ...prev, [name]: null }));
    };

    const handleEditToggle = () => {
        if (isEditMode) {
            setErrors({});
            handleCancel();
        } else {
            setIsEditMode(true);
            dispatch(restrictnavigate(true));
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const newErrors = validateForm(formData);

        if (Object.keys(newErrors).length === 0) {
            const genderPayload = formData.gender;

            const payload = {
                name: formData.name,
                surname: formData.surname,
                gender: genderPayload,
                dob: formData.dob,
                occupation: formData.occupation,
                email: formData.email,
                phone: formData.phone,
                altPhone: formData.altPhone,
                address: formData.address,
                additional: formData.additional,
                countryId: formData.countryId || selectedCountry?.countryId,
                city: formData.city,
                zipCode: formData.zipcode,
                timeZone: formData.timeZone,
            };

            if (emailOtpVerified) payload.emailVerified = true;
            if (phoneOtpVerified) payload.phoneNoVerified = true;

            // Check if all originally incomplete fields are now filled
            const allIncompleteFieldsFilled = isIncompleteMode && incompleteFields.every(field => {
                if (field === 'zipcode') return formData.zipcode?.trim() !== '';
                return formData[field]?.trim() !== '';
            });

            // If in incomplete mode and all incomplete fields are filled, update profileStatus to "complete"
            if (allIncompleteFieldsFilled) {
                payload.profileStatus = 'complete';
            }

            Swal.fire({
                title: t('patientprofiletab.confirm_update_title'),
                text: t('patientprofiletab.confirm_update_text'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#4ABDBD',
                cancelButtonColor: '#d33',
                confirmButtonText: t('patientprofiletab.confirm_update'),
            }).then((result) => {
                if (result.isConfirmed) {
                    dispatch(updatePatient({ patientid: patient.patientId, payload, keypass: '' }))
                        .unwrap()
                        .then(() => {
                            toast.success(t('patientprofiletab.updated_text'));
                            dispatch(restrictnavigate(false));
                            setIsEditMode(false);
                            setErrors({});

                            // Switch from incomplete mode to normal mode
                            if (allIncompleteFieldsFilled) {
                                setIsIncompleteMode(false);
                                setIncompleteFields([]);
                            }
                        })
                        .catch(() => {
                            toast.warn(t('patientprofiletab.error_text'));
                        });
                }
            });
        } else {
            setErrors(newErrors);
        }
    };

    const handleCancel = () => {
        setIsEditMode(false);
        dispatch(restrictnavigate(false));
        setErrors({});
        setPhoneStatus(null);
        resetOtpPhoneState();
        lastCheckedPhoneRef.current = '';
        setEmailStatus(null);
        resetOtpEmailState();
        lastCheckedEmailRef.current = '';
        const currentPatient = selectedPatient || patient;
        if (currentPatient) {
            // Normalize gender: convert "others" to "other" to match select dropdown values
            const normalizedGender = currentPatient.gender === 'others' ? 'other' : (currentPatient.gender || '');

            const matchedCountry = countryTimezoneList?.find(c =>
                c.countryName === currentPatient.country ||
                c.countryId === currentPatient.country?.countryId ||
                c.countryId === currentPatient.country ||
                c.countryId === currentPatient.countryId
            );

            setFormData({
                name: currentPatient.name || '',
                surname: currentPatient.surname || '',
                gender: normalizedGender,
                dob: toUTCDateInputValue(currentPatient.dob),
                age: currentPatient.dob ? calculateAge(currentPatient.dob) : 0,
                occupation: currentPatient.occupation || '',
                email: currentPatient.email || '',
                phone: currentPatient.phone || '',
                altPhone: currentPatient.altPhone || '',
                address: currentPatient.address || '',
                additional: currentPatient.additional || '',
                country: currentPatient.country?.countryName || currentPatient.country || '',
                countryId: matchedCountry?.countryId || currentPatient.country?.countryId || '',
                city: currentPatient.city || '',
                zipcode: currentPatient.zipCode || '',
                timeZone: currentPatient.timeZone || '',
            });
        }
    };

    // === Profile Image Modal Functions ===
    const handleEditClick = () => {
        setIsModalOpen(true);
        setSelectedFile(null);
        setImageSrc(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null);
        setIsCropping(false);
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
        if (!validTypes.includes(file.type)) {
            toast.warn(t('patientprofiletab.invalidfiletype'));
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            setImageSrc(reader.result);
            setSelectedFile(file);
            setIsCropping(true);
        };
        reader.readAsDataURL(file);
    };

    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const getCroppedImg = async (imageSrc, pixelCrop) => {
        const image = new Image();
        image.src = imageSrc;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;

        ctx.drawImage(
            image,
            pixelCrop.x,
            pixelCrop.y,
            pixelCrop.width,
            pixelCrop.height,
            0,
            0,
            pixelCrop.width,
            pixelCrop.height
        );

        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                resolve(blob);
            }, 'image/jpeg');
        });
    };

    const handleSubmitCrop = async () => {
        if (!croppedAreaPixels || !imageSrc) return;

        setIsUploading(true);
        try {
            const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
            const file = new File([croppedBlob], selectedFile.name, { type: 'image/jpeg' });
            const formData = new FormData();
            formData.append('file', file);

            await dispatch(patientProfileUpdate({ patientId: patient.patientId, formData })).unwrap();

            const newImageUrl = URL.createObjectURL(croppedBlob);
            setProfileImage(newImageUrl);

            toast.success(t('patientprofiletab.upload_success_text'));

            setIsModalOpen(false);
        } catch (err) {
            toast.warn(t('patientprofiletab.upload_error_text'));
        } finally {
            setIsUploading(false);
        }
    };

    const handleRemoveImage = async () => {
        Swal.fire({
            title: t('patientprofiletab.remove_image_title'),
            text: t('patientprofiletab.remove_image_text'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('patientprofiletab.remove'),
            confirmButtonColor: '#4ABDBD',
            cancelButtonText: t('patientprofiletab.cancel'),
        }).then(async (result) => {
            if (result.isConfirmed) {
                // Start API call
                const res = await dispatch(removePatientProfileImage(patient.patientId));

                // If success
                if (res?.payload?.status === true) {
                    setProfileImage(null);
                    setIsModalOpen(false);

                    toast.success(t('patientprofiletab.remove_success_text'));
                }
            }
        });
    };




    const closeModal = () => {
        setIsModalOpen(false);
        setImageSrc(null);
        setSelectedFile(null);
        setIsCropping(false);
    };

    const canShowEditButton = !readOnly && !isEditMode && (!isIncompleteMode || incompleteFields.length === 0);

    return (
        <div data-testid="patient-profile-tab" className="mt-4">
            <div data-testid="patient-profile-header" className="flex justify-end mb-6">
                {canShowEditButton && (
                    <button
                        data-testid="patient-profile-edit-toggle-button"
                        onClick={handleEditToggle}
                        className="group flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
                    >
                        <span className="relative z-10">
                            {t('patientprofiletab.edit_profile')}
                        </span>
                    </button>
                )}
            </div>

            {/* Incomplete Profile Alert */}
            {isIncompleteMode && incompleteFields.length > 0 && (
                <div className="mb-6 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-yellow-400 rounded-xl shadow-md" data-testid="incomplete-profile-alert">
                    <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                            <svg className="w-6 h-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="flex-1">
                            <h3 className="text-sm font-bold text-yellow-800 mb-1">
                                {t('patientprofiletab.incomplete_profile_title')}
                            </h3>
                            <p className="text-sm text-yellow-700">
                                {t('patientprofiletab.incomplete_profile_message')}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Profile Picture Section */}
            <div data-testid="profile-picture-section" className="flex justify-center my-6">
                <div data-testid="profile-picture-container" className="relative">
                    <div data-testid="profile-picture-wrapper" className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-gray-200 flex items-center justify-center border-4 border-gray-300 overflow-hidden">
                        {profileImage ? (
                            <img src={getFileURL(profileImage)} alt={t('patientprofiletab.profile')} className="w-full h-full object-cover" data-testid="profile-image" />
                        ) : (
                            <span className="text-gray-500 text-2xl sm:text-3xl font-medium" data-testid="profile-initial">
                                {formData?.name?.charAt(0)?.toUpperCase()}
                            </span>
                        )}
                    </div>
                    <button
                        onClick={handleEditClick}
                        disabled={isUploading || isLoading}
                        className={`absolute bottom-0 right-0 bg-[#4ABDBD] text-white p-1.5 rounded-b-full rounded-tr-full hover:bg-[#3AA8A8] transition-colors ${isUploading || isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${readOnly ? 'hidden' : ''}`}
                        data-testid="edit-profile-pic-button"
                    >
                        {isUploading || isLoading ? (
                            <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        ) : (
                            <Edit className="h-4 w-4" />
                        )}
                    </button>
                </div>
            </div>

            {/* === Profile Image Modal - Same as Admin/Doctor === */}
            {isModalOpen && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                    role="dialog"
                    aria-labelledby="profile-image-modal-title"
                    id="profile-image-modal"
                    data-testid="profile-image-modal"
                >
                    <div className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-md sm:max-w-lg md:max-w-2xl max-h-[80vh] sm:max-h-[90vh] flex flex-col overflow-hidden">
                        {/* Header - Sticky */}
                        <div className="p-4 sm:p-6 sticky top-0 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200">
                            <h2
                                className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent"
                                id="profile-image-modal-title"
                            >
                                {t('patientprofiletab.profile_image_update')}
                            </h2>
                            <button
                                data-testid="patient-profile-image-modal-close-button"
                                onClick={closeModal}
                                className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110"
                            >
                                <X className="w-5 h-5 sm:w-6 sm:h-6" />
                            </button>
                        </div>

                        {/* Scrollable Body */}
                        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar">
                            <div className="mb-4">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    {t('patientprofiletab.select_image')} <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <input
                                        id="admin-profile-image-upload"
                                        data-testid="patient-profile-image-file-input"
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileSelect}
                                        className="hidden"
                                    />
                                    <label
                                        htmlFor="admin-profile-image-upload"
                                        className="flex items-center gap-4 w-full p-3 rounded-xl border border-gray-300 hover:border-[#4ABDBD] focus-within:ring-2 focus-within:ring-[#4ABDBD] transition-all duration-300 cursor-pointer"
                                    >
                                        <span className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white text-sm font-semibold">
                                            {t('filestab.choose_file')}
                                        </span>
                                        <span className="text-sm text-gray-500 truncate">
                                            {selectedFile ? selectedFile.name : t('filestab.no_file_chosen')}
                                        </span>
                                    </label>
                                </div>
                            </div>

                            {imageSrc && (
                                <div className="relative h-64 bg-gray-100 rounded-xl overflow-hidden shadow-inner border border-gray-200 mb-4">
                                    <Cropper
                                        image={imageSrc}
                                        crop={crop}
                                        zoom={zoom}
                                        aspect={1}
                                        onCropChange={setCrop}
                                        onZoomChange={setZoom}
                                        onCropComplete={onCropComplete}
                                        cropShape="round"
                                        showGrid={false}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Footer - Sticky */}
                        <div className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-gradient-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4">
                            <button
                                data-testid="patient-profile-image-modal-cancel-button"
                                onClick={closeModal}
                                className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer"
                            >
                                {t('patientprofiletab.cancel')}
                            </button>

                            {profileImage && (
                                <button
                                    data-testid="patient-profile-image-modal-remove-button"
                                    onClick={handleRemoveImage}
                                    disabled={removeLoading}
                                    className={`px-4 py-2 bg-gradient-to-r from-red-400 to-red-600 text-white rounded-xl shadow-lg transition-all duration-300 font-semibold text-sm hover:scale-105 flex items-center justify-center gap-2 ${removeLoading ? "opacity-60 cursor-not-allowed" : ""
                                        }`}
                                >
                                    {removeLoading ? (
                                        <span className="border-2 border-white border-t-transparent rounded-full w-5 h-5 animate-spin"></span>
                                    ) : (
                                        t('patientprofiletab.remove')
                                    )}
                                </button>
                            )}

                            <button
                                data-testid="patient-profile-image-modal-submit-button"
                                onClick={handleSubmitCrop}
                                disabled={!isCropping || isUploading}
                                className={`px-4 py-2 rounded-xl text-white font-semibold text-sm shadow-lg transition-all duration-300 ${!isCropping || isUploading
                                    ? 'bg-gray-400 cursor-not-allowed opacity-60'
                                    : 'bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] hover:shadow-xl hover:scale-105'
                                    }`}
                            >
                                {isUploading ? (
                                    <span className="border-2 border-white border-t-transparent rounded-full w-5 h-5 animate-spin inline-block"></span>
                                ) : (
                                    profileImage
                                        ? t('patientprofiletab.update')
                                        : t('patientprofiletab.submit')
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <h2 data-testid="patient-profile-title" className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent mb-6">
                {t('patientprofiletab.basic_information')}
            </h2>
            <div data-testid="patient-profile-form" className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {/* No Mitra One Care id field: owner rule 29 Sep 2026, no
                    system id is drawn on screen. A hospital's own patient
                    number lives on the allocation, not here. */}

                {/* Blood group is a PERSON-level field (doc/one-patient-row.md
                    §2) that the patient sets in their own app, so it shows here
                    and is never editable from the platform. */}
                <div className="flex flex-col">
                    <label className="text-base font-semibold text-gray-500 mb-1">
                        {t('patientprofiletab.blood_group', 'Blood group')}
                    </label>
                    <p data-testid="patient-profile-blood-group" className="text-sm text-gray-500">
                        {patient?.bloodGroup || '-'}
                    </p>
                </div>

                {/* 1. Gender */}
                <div className="flex flex-col">
                    <label className="text-base font-semibold text-gray-500 mb-1">
                        {t('patientprofiletab.gender')} {isEditMode && <span className="text-red-500">*</span>}
                    </label>
                    {isEditMode ? (
                        <>
                            <GenderSelect
                                value={formData.gender}
                                onChange={(val) => {
                                    setFormData(prev => ({ ...prev, gender: val }));
                                    setErrors(prev => ({ ...prev, gender: null }));
                                }}
                                options={getGenderOptions(t)}
                                placeholder={t('patientprofiletab.select_gender')}
                                error={!!errors.gender}
                            />
                            {errors.gender && (
                                <p className="mt-2 text-xs text-red-500 font-medium">{errors.gender}</p>
                            )}
                        </>
                    ) : (
                        <p className="text-sm text-gray-500">
                            {formData.gender
                                ? (formData.gender === 'other'
                                    ? t('patientprofiletab.other')
                                    : t(`patientprofiletab.${formData.gender}`))
                                : '-'}
                        </p>
                    )}
                </div>

                {/* 2. First Name */}
                <div className="flex flex-col">
                    <label className="text-base font-semibold text-gray-500 mb-1">
                        {t('patientprofiletab.name')} {isEditMode && <span className="text-red-500">*</span>}
                    </label>
                    {isEditMode ? (
                        <input
                            data-testid="patient-profile-name-input"
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            maxLength={20}
                            className={`p-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] hover:border-[#4ABDBD] text-sm transition-all duration-300 ${errors.name ? 'border-red-500 bg-red-50' : ''}`}
                        />
                    ) : (
                        <p className="text-sm text-gray-500">{formData.name}</p>
                    )}
                    {errors.name && isEditMode && (
                        <p data-testid="patient-profile-name-error" className="mt-2 text-xs text-red-500 font-medium">{errors.name}</p>
                    )}
                </div>

                {/* 3. Surname */}
                <div className="flex flex-col">
                    <label className="text-base font-semibold text-gray-500 mb-1">
                        {t('patientprofiletab.surname')} {isEditMode && <span className="text-red-500">*</span>}
                    </label>
                    {isEditMode ? (
                        <input
                            data-testid="patient-profile-surname-input"
                            type="text"
                            name="surname"
                            value={formData.surname}
                            onChange={handleInputChange}
                            maxLength={20}
                            className={`p-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.surname ? 'border-red-500 bg-red-50' :
                                (isIncompleteMode && incompleteFields.includes('surname')) ? 'border-yellow-400 bg-yellow-50' :
                                    'border-gray-300 hover:border-[#4ABDBD]'
                                }`}
                        />
                    ) : (
                        <p className="text-sm text-gray-500 uppercase">{formData.surname}</p>
                    )}
                    {errors.surname && isEditMode && (
                        <p data-testid="patient-profile-surname-error" className="mt-2 text-xs text-red-500 font-medium">{errors.surname}</p>
                    )}
                </div>

                {/* Date of Birth */}
                <div className="flex flex-col">
                    <label className="text-base font-semibold text-gray-500 mb-1">
                        {t('patientprofiletab.dob')} {isEditMode && <span className="text-red-500">*</span>}
                    </label>
                    {isEditMode ? (
                        <>
                            <input
                                data-testid="patient-profile-dob-input"
                                type="date"
                                name="dob"
                                value={formData.dob}
                                onChange={handleInputChange}
                                min="1900-01-01"
                                max={new Date().toISOString().split('T')[0]}
                                className={`p-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.dob ? 'border-red-500 bg-red-50' :
                                    (isIncompleteMode && incompleteFields.includes('dob')) ? 'border-yellow-400 bg-yellow-50' :
                                        'border-gray-300 hover:border-[#4ABDBD]'
                                    }`}
                            />
                            {errors.dob && (
                                <p data-testid="patient-profile-dob-error" className="mt-2 text-xs text-red-500 font-medium">{errors.dob}</p>
                            )}
                        </>
                    ) : (
                        <p className="text-sm text-gray-500">{formatDobDisplay(formData.dob)}</p>
                    )}
                </div>

                {/* Age */}
                <div className="flex flex-col">
                    <label className="text-base font-semibold text-gray-500 mb-1">
                        {t('patientprofiletab.age')}
                    </label>
                    <p className="text-sm text-gray-500">{formData.age}</p>
                </div>

                {/* Occupation */}
                <div className="flex flex-col">
                    <label className="text-base font-semibold text-gray-500 mb-1">
                        {t('patientprofiletab.occupation')}
                    </label>
                    {isEditMode ? (
                        <input
                            data-testid="patient-profile-occupation-input"
                            type="text"
                            name="occupation"
                            value={formData.occupation}
                            onChange={handleInputChange}
                            maxLength={25}
                            className={`p-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.occupation ? 'border-red-500 bg-red-50' :
                                (isIncompleteMode && incompleteFields.includes('occupation')) ? 'border-yellow-400 bg-yellow-50' :
                                    'border-gray-300 hover:border-[#4ABDBD]'
                                }`}
                        />
                    ) : (
                        <p className="text-sm text-gray-500">{formData.occupation}</p>
                    )}
                    {errors.occupation && isEditMode && (
                        <p data-testid="patient-profile-occupation-error" className="mt-2 text-xs text-red-500 font-medium">{errors.occupation}</p>
                    )}
                </div>
            </div>

            <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent mb-6">
                {t('patientprofiletab.contact_information')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {/* Email */}
                <div className="flex flex-col">
                    <label className="text-base font-semibold text-gray-500 mb-1">
                        {t('patientprofiletab.email')}
                    </label>
                    {isEditMode && isAdminLevel ? (
                        <>
                            <div className="relative">
                                <input
                                    data-testid="patient-profile-email-input"
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={(e) => {
                                        handleInputChange(e);
                                        if (e.target.value !== formData.email) {
                                            setEmailStatus(null);
                                            resetOtpEmailState();
                                        }
                                    }}
                                    disabled={emailOtpVerified && (selectedPatient?.email || patient?.email) !== formData.email}
                                    maxLength={254}
                                    className={`w-full p-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.email ? 'border-red-500 bg-red-50' : emailStatus === false ? 'border-red-400 focus:border-red-400' : emailStatus === true && emailOtpVerified ? 'border-green-400 focus:border-green-400' : 'border-gray-300 hover:border-[#4ABDBD]'}`}
                                />
                                {formData.email && emailRegex.test(formData.email.trim()) && (selectedPatient?.email || patient?.email) !== formData.email && (
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                        {emailCheckLoading ? (
                                            <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
                                        ) : emailStatus === true ? (
                                            emailOtpVerified ? (
                                                <CheckCircle className="h-5 w-5 text-green-500" />
                                            ) : null
                                        ) : emailStatus === false ? (
                                            <X className="h-5 w-5 text-red-500" />
                                        ) : null}
                                    </div>
                                )}
                            </div>
                            {emailStatus === false && !errors.email && (
                                <p className="text-red-500 text-xs font-medium mt-1">{t('patientProfile.email_already_exists')}</p>
                            )}
                            {errors.email && (
                                <p data-testid="patient-profile-email-error" className="mt-2 text-xs text-red-500 font-medium">{errors.email}</p>
                            )}

                            {/* Email OTP Verification UI */}
                            {formData.email && emailStatus === true && (selectedPatient?.email || patient?.email) !== formData.email && !emailOtpVerified && (
                                <div className="mt-3 p-4 bg-gray-50 rounded-xl border border-gray-100 flex flex-col gap-3">
                                    <div className="flex justify-between items-center px-1">
                                        <span className="text-sm font-medium text-gray-700">{t('patientProfile.email_verification')}</span>
                                        {!emailOtpSent ? (
                                            <button
                                                type="button"
                                                data-testid="patient-profile-email-otp-send-button"
                                                onClick={handleSendEmailOtp}
                                                disabled={isLoading || sendOtpLoading}
                                                className="text-xs bg-[#4ABDBD]/10 text-[#4ABDBD] px-3 py-1.5 rounded-lg font-medium hover:bg-[#4ABDBD]/20 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                                            >
                                                <Send className="w-3.5 h-3.5" />
                                                {t('patientProfile.send_otp')}
                                            </button>
                                        ) : (
                                            <div className="flex items-center gap-2">
                                                {emailOtpTimer > 0 ? (
                                                    <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-1 rounded-md">
                                                        00:{emailOtpTimer < 10 ? `0${emailOtpTimer}` : emailOtpTimer}
                                                    </span>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        data-testid="patient-profile-email-otp-resend-button"
                                                        onClick={handleSendEmailOtp}
                                                        disabled={isLoading || sendOtpLoading}
                                                        className="text-xs text-[#4ABDBD] hover:text-[#3AA8A8] font-medium flex items-center gap-1 p-1 hover:bg-[#4ABDBD]/10 rounded transition-colors"
                                                    >
                                                        <RefreshCw className="w-3.5 h-3.5" />
                                                        {t('patientProfile.resend')}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {emailOtpSent && (
                                        <div className="relative flex items-center gap-2 mt-1">
                                            <input
                                                type="text"
                                                data-testid="patient-profile-email-otp-input"
                                                maxLength={6}
                                                value={emailOtpCode}
                                                onChange={(e) => {
                                                    const val = e.target.value.replace(/\D/g, '');
                                                    setEmailOtpCode(val);
                                                    if (val.length === 6) {
                                                        handleVerifyEmailOtp(val);
                                                    }
                                                }}
                                                placeholder={t('patientProfile.enter_6_digit_otp')}
                                                className={`flex-1 p-2.5 bg-white border rounded-lg text-sm text-center tracking-widest font-medium focus:outline-none focus:ring-2 transition-all shadow-sm ${emailOtpError ? 'border-red-300 focus:ring-red-200' : 'border-gray-300 focus:border-[#4ABDBD] focus:ring-[#4ABDBD]/20'}`}
                                                disabled={emailOtpVerifyingRef.current}
                                            />
                                            {emailOtpVerifyingRef.current && (
                                                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                                    <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {emailOtpError && (
                                        <p className="text-red-500 text-xs font-medium px-1 flex items-center gap-1 mt-0.5">
                                            <X className="w-3 h-3" />
                                            {emailOtpError}
                                        </p>
                                    )}
                                </div>
                            )}
                            {emailOtpVerified && (selectedPatient?.email || patient?.email) !== formData.email && (
                                <p className="text-green-600 text-sm mt-2 flex items-center gap-1.5 font-medium px-1">
                                    <CheckCircle className="w-4 h-4" /> {t('patientProfile.email_verified')}
                                </p>
                            )}
                        </>
                    ) : (
                        <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm text-gray-500">{formData.email}</p>
                            {(patient?.emailVerified || selectedPatient?.emailVerified) ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-600 border border-green-200">
                                    <CheckCircle className="w-3 h-3" /> {t('patientprofiletab.verified', 'Verified')}
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-500 border border-red-200">
                                    <XCircle className="w-3 h-3" /> {t('patientprofiletab.not_verified', 'Not Verified')}
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {/* Phone — patients can edit only when profile is incomplete and phone is missing; admin can always edit */}
                <div className="flex flex-col">
                    <label className="text-base font-semibold text-gray-500 mb-1">
                        {t('patientprofiletab.phone')} {isEditMode && (isAdminLevel || (isIncompleteMode && incompleteFields.includes('phone'))) && <span className="text-red-500">*</span>}
                    </label>
                    {isEditMode && (isAdminLevel || (isIncompleteMode && incompleteFields.includes('phone'))) ? (
                        <>
                            <div className="relative">
                                <div className={`flex items-center border rounded-xl p-2 transition-all duration-300 ${errors.phone ? 'border-red-500 bg-red-50' : phoneStatus === false ? 'border-red-400 focus-within:border-red-400' : phoneStatus === true && phoneOtpVerified ? 'border-green-400 focus-within:border-green-400' : (isIncompleteMode && incompleteFields.includes('phone')) ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300 hover:border-[#4ABDBD] focus-within:border-[#4ABDBD]'}`}>
                                    <PhoneCountryDropdown
                                        countryTimezoneList={countryTimezoneList}
                                        countryId={formData.countryId}
                                        onSelect={({ countryId, timeZone, countryName }) => {
                                            setFormData(prev => ({ ...prev, countryId, timeZone, country: countryName, phone: '', altPhone: '' }));
                                            setErrors(prev => ({ ...prev, countryId: null, timeZone: null }));
                                            setPhoneStatus(null);
                                            resetOtpPhoneState();
                                        }}
                                        disabled={phoneOtpVerified}
                                    />
                                    <input
                                        data-testid="patient-profile-phone-input"
                                        type="tel"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={(e) => {
                                            handleInputChange(e);
                                            // Reset OTP states if user modifies phone manually
                                            if (e.target.value.replace(/[^0-9]/g, '') !== formData.phone) {
                                                setPhoneStatus(null);
                                                resetOtpPhoneState();
                                            }
                                        }}
                                        maxLength={selectedCountry?.mobileMaxLength || 10}
                                        className="w-full p-2 focus:outline-none text-sm bg-transparent"
                                        placeholder={t('patientprofiletab.phone_placeholder')}
                                    />
                                </div>
                                {formData.phone && new RegExp(`^\\d{${selectedCountry?.mobileMinLength || 10},${selectedCountry?.mobileMaxLength || 10}}$`).test(formData.phone) && phoneStatus !== null && (selectedPatient?.phone || patient?.phone) !== formData.phone && (
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                        {mobileCheckLoading ? (
                                            <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
                                        ) : phoneStatus ? (
                                            <CheckCircle className="h-5 w-5 text-green-500" />
                                        ) : (
                                            <X className="h-5 w-5 text-red-500" />
                                        )}
                                    </div>
                                )}
                            </div>
                            {phoneStatus === false && !errors.phone && (
                                <p className="text-red-500 text-xs font-medium mt-1">{t('patientProfile.phone_already_exists')}</p>
                            )}
                            {errors.phone && (
                                <p data-testid="patient-profile-phone-error" className="mt-2 text-xs text-red-500 font-medium">{errors.phone}</p>
                            )}

                            {/* OTP Verification UI */}
                            {formData.phone && phoneStatus === true && (selectedPatient?.phone || patient?.phone) !== formData.phone && !phoneOtpVerified && (
                                <div className="mt-3 p-4 bg-gray-50 rounded-xl border border-gray-100 flex flex-col gap-3">
                                    <div className="flex justify-between items-center px-1">
                                        <span className="text-sm font-medium text-gray-700">{t('patientProfile.phone_verification')}</span>
                                        {!phoneOtpSent ? (
                                            <button
                                                type="button"
                                                data-testid="patient-profile-phone-otp-send-button"
                                                onClick={handleSendPhoneOtp}
                                                disabled={isLoading}
                                                className="text-xs bg-[#4ABDBD]/10 text-[#4ABDBD] px-3 py-1.5 rounded-lg font-medium hover:bg-[#4ABDBD]/20 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                                            >
                                                <Send className="w-3.5 h-3.5" />
                                                {t('patientProfile.send_otp')}
                                            </button>
                                        ) : (
                                            <div className="flex items-center gap-2">
                                                {phoneOtpTimer > 0 ? (
                                                    <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-1 rounded-md">
                                                        00:{phoneOtpTimer < 10 ? `0${phoneOtpTimer}` : phoneOtpTimer}
                                                    </span>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        data-testid="patient-profile-phone-otp-resend-button"
                                                        onClick={handleSendPhoneOtp}
                                                        disabled={isLoading}
                                                        className="text-xs text-[#4ABDBD] hover:text-[#3AA8A8] font-medium flex items-center gap-1 p-1 hover:bg-[#4ABDBD]/10 rounded transition-colors"
                                                    >
                                                        <RefreshCw className="w-3.5 h-3.5" />
                                                        {t('patientProfile.resend')}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {phoneOtpSent && (
                                        <div className="relative flex items-center gap-2 mt-1">
                                            <input
                                                type="text"
                                                data-testid="patient-profile-phone-otp-input"
                                                maxLength={6}
                                                value={phoneOtpCode}
                                                onChange={(e) => {
                                                    const val = e.target.value.replace(/\D/g, '');
                                                    setPhoneOtpCode(val);
                                                    if (val.length === 6) {
                                                        handleVerifyPhoneOtp(val);
                                                    }
                                                }}
                                                placeholder={t('patientProfile.enter_6_digit_otp')}
                                                className={`flex-1 p-2.5 bg-white border rounded-lg text-sm text-center tracking-widest font-medium focus:outline-none focus:ring-2 transition-all shadow-sm ${phoneOtpError ? 'border-red-300 focus:ring-red-200' : 'border-gray-300 focus:border-[#4ABDBD] focus:ring-[#4ABDBD]/20'}`}
                                                disabled={phoneOtpVerifyingRef.current}
                                            />
                                            {phoneOtpVerifyingRef.current && (
                                                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                                    <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {phoneOtpError && (
                                        <p className="text-red-500 text-xs font-medium px-1 flex items-center gap-1 mt-0.5">
                                            <X className="w-3 h-3" />
                                            {phoneOtpError}
                                        </p>
                                    )}
                                </div>
                            )}
                            {phoneOtpVerified && (selectedPatient?.phone || patient?.phone) !== formData.phone && (
                                <p className="text-green-600 text-sm mt-2 flex items-center gap-1.5 font-medium px-1">
                                    <CheckCircle className="w-4 h-4" /> {t('patientProfile.phone_verified')}
                                </p>
                            )}
                        </>
                    ) : (
                        <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm text-gray-500">{formData.phone || '-'}</p>
                            {(patient?.phoneNoVerified || selectedPatient?.phoneNoVerified) ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-600 border border-green-200">
                                    <CheckCircle className="w-3 h-3" /> {t('patientprofiletab.verified', 'Verified')}
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-500 border border-red-200">
                                    <XCircle className="w-3 h-3" /> {t('patientprofiletab.not_verified', 'Not Verified')}
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {/* Alternate mobile no */}
                <div className="flex flex-col">
                    <label className="text-base font-semibold text-gray-500 mb-1">
                        {t('patientprofiletab.alt_phone', 'Alternate mobile no')}
                    </label>
                    {isEditMode ? (
                        <>
                            <input
                                data-testid="patient-profile-alt-phone-input"
                                type="tel"
                                name="altPhone"
                                value={formData.altPhone}
                                onChange={handleInputChange}
                                maxLength={selectedCountry?.altMaxLength || selectedCountry?.mobileMaxLength || 10}
                                className={`p-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.altPhone ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD]'}`}
                                placeholder={t('patientprofiletab.phone_placeholder')}
                            />
                            {errors.altPhone && (
                                <p data-testid="patient-profile-alt-phone-error" className="mt-2 text-xs text-red-500 font-medium">{errors.altPhone}</p>
                            )}
                        </>
                    ) : (
                        <p className="text-sm text-gray-500">{formData.altPhone || '-'}</p>
                    )}
                </div>

                {/* Address */}
                <div className="flex flex-col">
                    <label className="text-base font-semibold text-gray-500 mb-1">
                        {t('patientprofiletab.address')} {isEditMode && <span className="text-red-500">*</span>}
                    </label>
                    {isEditMode ? (
                        <input
                            data-testid="patient-profile-address-input"
                            type="text"
                            name="address"
                            value={formData.address}
                            onChange={handleInputChange}
                            maxLength={50}
                            className={`p-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.address ? 'border-red-500 bg-red-50' :
                                (isIncompleteMode && incompleteFields.includes('address')) ? 'border-yellow-400 bg-yellow-50' :
                                    'border-gray-300 hover:border-[#4ABDBD]'
                                }`}
                        />
                    ) : (
                        <p className="text-sm text-gray-500 break-words">{formData.address}</p>
                    )}
                    {errors.address && isEditMode && (
                        <p data-testid="patient-profile-address-error" className="mt-2 text-xs text-red-500 font-medium">{errors.address}</p>
                    )}
                </div>

                {/* The desk's own note. It lives on the ALLOCATION
                    (patient_hospitals.note) since one-patient-row, so it is a
                    hospital's line about the patient rather than part of the
                    profile — the platform's read of the person does not show
                    it, and there is no tenant here to attribute it to. */}
                {!readOnly && (
                <div className="flex flex-col">
                    <label className="text-base font-semibold text-gray-500 mb-1">
                        {t('patientprofiletab.additional')}
                    </label>
                    {isEditMode ? (
                        <input
                            data-testid="patient-profile-additional-input"
                            type="text"
                            name="additional"
                            value={formData.additional}
                            onChange={handleInputChange}
                            maxLength={80}
                            className={`p-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] hover:border-[#4ABDBD] text-sm transition-all duration-300 ${errors.additional ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                }`}
                        />
                    ) : (
                        <p className="text-sm text-gray-500 break-words">{formData.additional}</p>
                    )}
                    {errors.additional && isEditMode && (
                        <p data-testid="patient-profile-additional-error" className="mt-2 text-xs text-red-500 font-medium">{errors.additional}</p>
                    )}
                </div>
                )}

                {/* Postal code */}
                <div className="flex flex-col">
                    <label className="text-base font-semibold text-gray-500 mb-1">
                        {t('patientprofiletab.zipcode')} {isEditMode && <span className="text-red-500">*</span>}
                    </label>
                    {isEditMode ? (
                        <>
                            <input
                                data-testid="patient-profile-zipcode-input"
                                type="text"
                                name="zipcode"
                                value={formData.zipcode}
                                onChange={handleInputChange}
                                maxLength={Math.min(selectedCountry?.zipMaxLength || 6, POSTAL_CODE_MAX_LENGTH)}
                                className={`p-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.zipcode ? 'border-red-500 bg-red-50' :
                                    (isIncompleteMode && incompleteFields.includes('zipcode')) ? 'border-yellow-400 bg-yellow-50' :
                                        'border-gray-300 hover:border-[#4ABDBD]'
                                    }`}
                                placeholder={t('patientprofiletab.zipcode_placeholder')}
                            />
                            {errors.zipcode && (
                                <p data-testid="patient-profile-zipcode-error" className="mt-2 text-xs text-red-500 font-medium">{errors.zipcode}</p>
                            )}
                        </>
                    ) : (
                        <p className="text-sm text-gray-500">{formData.zipcode || '-'}</p>
                    )}
                </div>

                {/* City */}
                <div className="flex flex-col">
                    <label className="text-base font-semibold text-gray-500 mb-1">
                        {t('patientprofiletab.city')} {isEditMode && <span className="text-red-500">*</span>}
                    </label>
                    {isEditMode ? (
                        <>
                            <input
                                data-testid="patient-profile-city-input"
                                type="text"
                                name="city"
                                value={formData.city}
                                onChange={handleInputChange}
                                maxLength={25}
                                className={`p-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.city ? 'border-red-500 bg-red-50' :
                                    (isIncompleteMode && incompleteFields.includes('city')) ? 'border-yellow-400 bg-yellow-50' :
                                        'border-gray-300 hover:border-[#4ABDBD]'
                                    }`}
                                placeholder={t('patientprofiletab.city_placeholder')}
                            />
                            {errors.city && (
                                <p data-testid="patient-profile-city-error" className="mt-2 text-xs text-red-500 font-medium">{errors.city}</p>
                            )}
                        </>
                    ) : (
                        <p className="text-sm text-gray-500">{formData.city || '-'}</p>
                    )}
                </div>

                {/* Country (auto-syncs with phone country code via shared formData.countryId) */}
                <div className="flex flex-col">
                    <label className="text-base font-semibold text-gray-500 mb-1">
                        {t('patientprofiletab.country')}
                    </label>
                    {isEditMode && isAdminLevel ? (
                        <CountryTimeZoneSelect
                            countryTimezoneList={countryTimezoneList}
                            countryId={formData.countryId}
                            timeZone={formData.timeZone}
                            onChange={({ countryId, timeZone }) => {
                                setFormData(prev => ({ ...prev, countryId, timeZone, phone: '', zipcode: '' }));
                                setErrors(prev => ({ ...prev, countryId: null, timeZone: null }));
                            }}
                            placeholder={t('patientprofiletab.select_country', 'Select Country')}
                            error={!!errors.countryId || !!errors.timeZone}
                        />
                    ) : (
                        <div className="flex items-center gap-2">
                            <CountryFlag countryName={selectedCountry?.countryName || patient?.country?.countryName} />
                            <p className="text-sm text-gray-500">
                                <MasterLabel type="masterCountry" id={selectedCountry?.countryId} field="countryName" fallback={selectedCountry?.countryName || formData.country || '-'} />
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {isEditMode && (
                <div className="flex justify-end gap-4">
                    {!isIncompleteMode && (
                        <button
                            data-testid="patient-profile-cancel-button"
                            onClick={handleCancel}
                            disabled={isLoading}
                            className={`px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                            {t('patientprofiletab.cancel')}
                        </button>
                    )}
                    <button
                        data-testid="patient-profile-update-button"
                        onClick={handleSubmit}
                        disabled={isLoading || (isIncompleteMode && !areAllIncompleteFieldsFilled())}
                        className={`px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm ${isLoading || (isIncompleteMode && !areAllIncompleteFieldsFilled())
                            ? 'opacity-50 cursor-not-allowed'
                            : 'hover:scale-105 cursor-pointer'
                            }`}
                    >
                        {isLoading ? (
                            <span className="border-2 border-white border-t-transparent rounded-full w-5 h-5 animate-spin inline-block"></span>
                        ) : (
                            t('patientprofiletab.update')
                        )}
                    </button>
                </div>
            )}
        </div>
    );
};

export default PatientProfileTab;
